import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { StatusBadge, getStatusLabel, getStatusIcon } from '../components/StatusBadge';
import { Modal } from '../components/Modal';
import { AddLogModal } from '../components/AddLogModal';
import { PaymentModal } from '../components/PaymentModal';
import { toast } from '../stores/toasts';
import { formatUzs, formatDate, timeAgo } from '../lib/format';
import { ORDER_STATUS_FLOW } from '@autoservice/shared';
import { useState } from 'react';
import type { Status } from '../types';

const LOG_LABEL: Record<string, string> = {
  WORK: '🔧 Ish',
  PART: '🛠 Ehtiyot qism',
  NOTE: '📝 Izoh',
};

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [addLog, setAddLog] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [statusModal, setStatusModal] = useState<Status | null>(null);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.getOrder(id!),
    enabled: !!id,
  });

  const updateStatus = useMutation({
    mutationFn: (status: Status) => api.updateOrderStatus(id!, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['today-stats'] });
      toast.success('Status yangilandi');
      setStatusModal(null);
    },
    onError: (e: any) => toast.error(e?.message || 'Xatolik'),
  });

  if (isLoading) return <div className="spinner" />;
  if (!order) return <div className="empty">Buyurtma topilmadi</div>;

  const total = order.total ?? order.totalCached ?? 0;
  const paid = order.paid ?? (order.payments ?? []).filter((p) => p.status === 'COMPLETED').reduce((s, p) => s + p.amount, 0);
  const balance = order.balance ?? Math.max(0, total - paid);
  const allowedNext = ORDER_STATUS_FLOW[order.status] ?? [];

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 10 }}>
        ← Orqaga
      </button>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>🚗 {order.car.plate}</div>
          <StatusBadge status={order.status} />
        </div>
        <div className="muted">
          {order.car.brand} {order.car.model}
          {order.car.year ? ` · ${order.car.year}` : ''}
        </div>
      </div>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 6 }}>
          <div>
            <div style={{ fontWeight: 600 }}>👤 {order.client.name}</div>
            <a href={`tel:${order.client.phone}`} className="muted">📞 {order.client.phone}</a>
          </div>
          <a href={`tel:${order.client.phone}`} className="btn btn-primary btn-sm">📞 Qo'ng'iroq</a>
        </div>
      </div>

      <div className="card">
        <div className="field-label" style={{ marginBottom: 6 }}>Muammo</div>
        <div>{order.problem}</div>
        {order.mileageAtIntake != null && (
          <div className="muted" style={{ marginTop: 6 }}>📏 Probeg: {order.mileageAtIntake.toLocaleString('uz-UZ')} km</div>
        )}
        {order.notesInternal && (
          <>
            <div className="divider" />
            <div className="field-label" style={{ marginBottom: 6 }}>Ichki eslatma</div>
            <div className="muted">{order.notesInternal}</div>
          </>
        )}
      </div>

      <div className="card">
        <div className="row-between">
          <div>
            <div className="field-label">Jami</div>
            <div className="amount amount-big">{formatUzs(total)}</div>
          </div>
          <div>
            <div className="field-label">To'langan</div>
            <div className="amount amount-big" style={{ color: 'var(--success)' }}>{formatUzs(paid)}</div>
          </div>
          <div>
            <div className="field-label">Qarz</div>
            <div className="amount amount-big" style={{ color: balance > 0 ? 'var(--danger)' : 'var(--success)' }}>
              {formatUzs(balance)}
            </div>
          </div>
        </div>
        {balance > 0 && (
          <button className="btn btn-primary btn-block" onClick={() => setPayOpen(true)} style={{ marginTop: 12 }}>
            💰 To'lov qabul qilish
          </button>
        )}
      </div>

      {allowedNext.length > 0 && (
        <div className="card">
          <div className="field-label" style={{ marginBottom: 8 }}>Status o'zgartirish</div>
          <div className="chips">
            {allowedNext.map((s) => (
              <div
                key={s}
                className="chip"
                onClick={() => {
                  if (s === 'DELIVERED' || s === 'CANCELLED') setStatusModal(s);
                  else updateStatus.mutate(s);
                }}
                style={{
                  cursor: 'pointer',
                  borderColor: s === 'CANCELLED' ? 'var(--danger)' : undefined,
                  color: s === 'CANCELLED' ? 'var(--danger)' : undefined,
                }}
              >
                {getStatusIcon(s)} {getStatusLabel(s)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="row" style={{ marginBottom: 10 }}>
        <button className="btn btn-primary btn-block" onClick={() => setAddLog(true)}>
          ➕ Log qo'shish
        </button>
      </div>

      <h3>📜 Tarix</h3>
      {order.logs && order.logs.length > 0 ? (
        <div className="timeline">
          {order.logs.map((log) => (
            <div key={log.id} className={`timeline-item ${log.type}`}>
              <div className="timeline-head">
                <span>{LOG_LABEL[log.type] ?? log.type}</span>
                <span>{formatDate(log.createdAt)}</span>
              </div>
              <div className="timeline-body">
                {log.description}
                {log.type !== 'NOTE' && log.totalPrice > 0 && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {log.qty} × {log.unitPrice.toLocaleString('uz-UZ')} = <b className="amount">{formatUzs(log.totalPrice)}</b>
                  </div>
                )}
                {log.performedBy?.name && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {log.performedBy.name} · {timeAgo(log.createdAt)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty">
          <div className="emoji">📭</div>
          <div>Hozircha loglar yo'q</div>
        </div>
      )}

      <div className="muted" style={{ textAlign: 'center', marginTop: 20, fontSize: 11 }}>
        Qabul: {formatDate(order.receivedAt)}
        {order.completedAt && ` · Tugatildi: ${formatDate(order.completedAt)}`}
        {order.deliveredAt && ` · Topshirildi: ${formatDate(order.deliveredAt)}`}
      </div>

      <AddLogModal
        open={addLog}
        onClose={() => setAddLog(false)}
        orderId={order.id}
        onAdded={() => qc.invalidateQueries({ queryKey: ['order', id] })}
      />
      <PaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        orderId={order.id}
        balance={balance}
        onAdded={() => qc.invalidateQueries({ queryKey: ['order', id] })}
      />
      <Modal
        open={!!statusModal}
        onClose={() => setStatusModal(null)}
        title={`Status: ${statusModal ? getStatusLabel(statusModal) : ''}`}
        footer={
          statusModal && (
            <>
              <button className="btn" onClick={() => setStatusModal(null)}>Yo'q</button>
              <button
                className={`btn ${statusModal === 'CANCELLED' ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => updateStatus.mutate(statusModal)}
                disabled={updateStatus.isPending}
              >
                Ha, tasdiqlash
              </button>
            </>
          )
        }
      >
        <p>Buyurtmani <b>{statusModal ? getStatusLabel(statusModal) : ''}</b> ga o'tkazmoqchimisiz?</p>
      </Modal>
    </div>
  );
}
